import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { representantesAPI } from '../../services/api';

const RepresentantesList = () => {
  const [representantes, setRepresentantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');

  useEffect(() => {
    loadRepresentantes();
  }, [tipoFilter]);

  const loadRepresentantes = async () => {
    try {
      setLoading(true);
      const params = {};
      if (tipoFilter) params.tipo = tipoFilter;
      if (search.trim()) params.search = search.trim();
      const response = await representantesAPI.getAll(params);
      setRepresentantes(response.data);
    } catch (error) {
      console.error('Error cargando representantes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadRepresentantes();
  };

  const tipoLabel = {
    padre: 'Padre',
    madre: 'Madre',
    otro: 'Otro'
  };

  const tipoColor = {
    padre: 'bg-blue-100 text-blue-700',
    madre: 'bg-pink-100 text-pink-700',
    otro: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Representantes</h1>
        <span className="text-sm text-gray-500">{representantes.length} representantes</span>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por nombre, apellido o cédula..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={tipoFilter}
              onChange={(e) => setTipoFilter(e.target.value)}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
            >
              <option value="">Todos</option>
              <option value="padre">Padres</option>
              <option value="madre">Madres</option>
              <option value="otro">Otros</option>
            </select>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              Buscar
            </button>
          </div>
        </form>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : representantes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <span className="text-4xl">👨‍👩‍👦</span>
          <p className="text-gray-500 mt-4">No se encontraron representantes</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Representante</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Cédula</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Tipo</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Teléfono</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Jugadores</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {representantes.map((rep) => (
                  <tr key={rep.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-800">{rep.nombre} {rep.apellido}</p>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{rep.cedula}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${tipoColor[rep.tipo] || 'bg-gray-100 text-gray-700'}`}>
                        {tipoLabel[rep.tipo] || rep.tipo}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{rep.telefono || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{rep.email || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{rep.jugadores_count || 0}</td>
                    <td className="py-3 px-4">
                      <Link to={`/admin/representantes/${rep.id}`} className="text-blue-600 hover:underline text-sm">
                        Ver →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {representantes.map((rep) => (
              <Link key={rep.id} to={`/admin/representantes/${rep.id}`} className="block bg-white rounded-xl shadow-lg p-4 hover:shadow-xl transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-gray-800">{rep.nombre} {rep.apellido}</p>
                    <p className="text-sm text-gray-500">{rep.cedula}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${tipoColor[rep.tipo] || 'bg-gray-100 text-gray-700'}`}>
                    {tipoLabel[rep.tipo] || rep.tipo}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  {rep.telefono && <span>📞 {rep.telefono}</span>}
                  {rep.email && <span>✉️ {rep.email}</span>}
                  <span>👦 {rep.jugadores_count || 0} jugador(es)</span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RepresentantesList;
